// @ts-strict-ignore
import React, { useRef, useState, useEffect } from 'react'
import { View, ViewStyle, FlatList } from 'react-native'
import MaterialIcon from 'react-native-vector-icons/MaterialIcons'
import I18n from 'APP/I18n'
import { navigateToCms, getContentType } from 'APP/Lib/ContentHelpers'
import ContentCard from 'APP/Converse/ContentCard'
import ContentIcon from 'APP/Components/ContentIcon'
import { useTheme } from 'styled-components/native'
import { isWeb } from 'APP/Helpers/checkPlatform'
import { Document, Video, Audio } from 'APP/Store/Content/types'
import {
  StyledHeaderContainer,
  StyledFlatList,
  StyledHeaderText,
  StyledScrollLeftButton,
  StyledScrollRightButton,
  LIST_ITEM_MARGIN,
} from './style'

export type CMSContentItem = Document | Video | Audio

interface AppComponentData {
  documents?: CMSContentItem[]
}

interface AppComponent {
  data?: AppComponentData
}

export interface DiscoverNewContentProps {
  appComponent?: AppComponent
  style?: ViewStyle
}

const DiscoverNewContent = ({ appComponent, style }: DiscoverNewContentProps) => {
  const flatListRef = useRef<FlatList<CMSContentItem>>(null)
  const [index, setIndex] = useState(0)

  const theme = useTheme()
  const listItemMargin = LIST_ITEM_MARGIN({ theme })

  const MAX_WIDTH = 300

  useEffect(() => {
    flatListRef.current?.scrollToIndex({
      index: index,
      animated: true,
      viewOffset: -listItemMargin, // Account for  the margin around the list item
    })
  }, [index, listItemMargin])

  const documents = appComponent?.data?.documents
  if (!documents || !documents.length) return null

  const leftScrollDisabled = index === 0
  const rightScrollDisabled = index === documents.length - 1

  const scrollLeft = () => {
    setIndex(Math.max(0, index - 1))
  }
  const scrollRight = () => {
    setIndex(Math.min(documents.length - 1, index + 1))
  }

  const handleOnPress = (item: CMSContentItem, title: string) => {
    navigateToCms(item, title, 'DiscoverNewContentScreen', false)
  }

  const renderListItem = (item: CMSContentItem) => {
    const title = item.title
    const length = `${item.length} ${I18n.t('DiscoverNewContent.length.minutes')}`
    const difficulty = item.difficulty
    const info = difficulty ? `${length} ⋅ ${difficulty}` : length

    return (
      <ContentCard
        title={title}
        info={info}
        icon={<ContentIcon type={getContentType(item)} />}
        imageUrl={item.thumbnail?.url}
        notice={item.notice}
        onPress={() => handleOnPress(item, title)}
        testID={'DOCUMENT_ITEM'}
        maxWidth={MAX_WIDTH}
      />
    )
  }

  return (
    <View style={style}>
      <StyledHeaderContainer>
        <StyledHeaderText>{I18n.t('DiscoverNewContent.title')}</StyledHeaderText>
        {isWeb() && (
          <>
            <StyledScrollLeftButton
              disabled={leftScrollDisabled}
              onPress={scrollLeft}
              testID="SCROLL_LEFT"
              accessibilityRole="button"
              accessibilityLabel={I18n.t('DiscoverNewContent.scrollLeftAccessibilityLabel')}
            >
              <MaterialIcon
                name="chevron-left"
                size={24}
                color={leftScrollDisabled ? theme.colors.textDisabled : theme.colors.text}
                accessibilityRole="image"
              />
            </StyledScrollLeftButton>
            <StyledScrollRightButton
              disabled={rightScrollDisabled}
              onPress={scrollRight}
              testID="SCROLL_RIGHT"
              accessibilityRole="button"
              accessibilityLabel={I18n.t('DiscoverNewContent.scrollRightAccessibilityLabel')}
            >
              <MaterialIcon
                name="chevron-right"
                size={24}
                color={rightScrollDisabled ? theme.colors.textDisabled : theme.colors.text}
                accessibilityRole="image"
              />
            </StyledScrollRightButton>
          </>
        )}
      </StyledHeaderContainer>
      <StyledFlatList
        ref={flatListRef}
        data={documents}
        horizontal
        showsHorizontalScrollIndicator={false}
        testID="DISCOVER_NEW_CONTENT_LIST"
        keyExtractor={(item: CMSContentItem) => item.id}
        renderItem={({ item }: { item: CMSContentItem }) => renderListItem(item)}
        getItemLayout={(_: any, index: number) => {
          const itemWidthWithMargin = MAX_WIDTH + listItemMargin * 2
          return {
            length: itemWidthWithMargin,
            offset: itemWidthWithMargin * index,
            index,
          }
        }}
        accessibilityLabel={I18n.t('DiscoverNewContent.title')}
      />
    </View>
  )
}

export default DiscoverNewContent
